import { Link } from 'react-router-dom';
import {MapPin, Award} from 'lucide-react';
import {
  CANDIDATE_QUALIFICATIONS,
  FRENCH_REGIONS,
  type CandidateQualification } from
'@/hooks/useAvailableCandidates';
import { buildCvthequePath } from '@/lib/cvthequeUrl';

/**
 * Bloc de maillage interne SEO pour la CVthèque.
 *
 * Objectif : exposer de vrais liens <Link> crawlables vers toutes les pages
 * indexables /disponibles/:qualif et /disponibles/:qualif/:region afin de
 * booster leur découverte et leur indexation par les moteurs.
 *
 * - Section 1 : un lien par qualification (BNSSA, BPJEPS AAN, BEESAN, STAPS…).
 * - Section 2 : pour la qualification courante (ou BNSSA par défaut), un lien
 *   par région française, pour mailler les 18 combinaisons qualif × région.
 */
export default function CvthequeInternalLinks({
  currentQualif,
  currentRegion



}: {currentQualif?: CandidateQualification | null;currentRegion?: string | null;}) {
  // Qualification de référence pour la liste des régions : celle de la page,
  // sinon la première qualification (BNSSA) pour offrir un maillage par défaut.
  const regionQualif: CandidateQualification =
  currentQualif ?? CANDIDATE_QUALIFICATIONS[0];

  return (
    <nav
      aria-label="Parcourir les profils disponibles par qualification et région"
      className="bg-white border-t border-gray-100">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        {/* Qualifications */}
        <div>
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-gray-500">
            <Award className="w-4 h-4 text-gray-400" />
            Profils disponibles par qualification
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {CANDIDATE_QUALIFICATIONS.map((q) => {
              const active = currentQualif === q && !currentRegion;
              return (
                <Link
                  key={q}
                  to={buildCvthequePath(q)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors ${
                  active ?
                  'border-gray-900 bg-gray-900 text-white' :
                  'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'}`
                  }>

                  {q} disponibles
                </Link>);

            })}
          </div>
        </div>

        {/* Régions pour la qualification de référence */}
        <div className="mt-8">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-gray-500">
            <MapPin className="w-4 h-4 text-gray-400" />
            {regionQualif} disponibles par région
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {FRENCH_REGIONS.map((r) => {
              const active = currentQualif === regionQualif && currentRegion === r;
              return (
                <Link
                  key={r}
                  to={buildCvthequePath(regionQualif, r)}
                  className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  active ?
                  'border-blue-600 bg-blue-50 text-blue-700' :
                  'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`
                  }>

                  {regionQualif} en {r}
                </Link>);

            })}
          </div>
        </div>
      </div>
    </nav>);

}
